/**
 * @author dhonehi
 * Данный composable подключается в каждом офере. При первом обращении к данному composable начинается загрузка избранных оферов.
 * Избранные оферы передются через поле items, которое может принимать функцию, котора должна возвращать объект или массив.
 *
 * Объект должен быть вида {id_товара: значение'}
 * Массив должен быть вида [id_товара]
 *
 * Если в настройках избранного указано поле pollingRate, то избранное будет автоматически обновляться каждые pollingRate секунд.
 * При этом если загрузка избранного заняла больше времени, чем pollingRate, то в следующий раз избранное обновится только
 * после того как завершится текущая загрузка и после истечения pollingRate.
 *
 * При добавлении / удалении товара в избранное, вызывается функция onChangeFavouriteStatus,
 * которая в свою очередь вызывает callback, передаваемый в настройках избранного. В данный callback передается payload,
 * который содержит информацию о товаре и поле isFavourite, которое хранит информацию о том, офер в избранном или нет.
 * Если callback ничего не возвращает, то новое значение избранного будет равно противоположному.
 */

import { ref, watch } from '@vue/composition-api';

import ApiEventController from '@/init/ApiEventController';

const favouritesData = {
    favourites: null,
    settings: null,
};

let isLoadedFavourites = false;
let isLoadingFavourites = false;

let favouritesPollingIntervalId = null;

/** Загружает избранное*/
async function loadFavourites() {
    isLoadingFavourites = true;

    try {
        favouritesData.favourites.value = null;

        if (typeof favouritesData.settings.items === 'function') {
            favouritesData.favourites.value = await favouritesData.settings.items();
        } else {
            throw new Error('Favourites items must be a function');
        }
    } catch (error) {
        console.error(error);
    } finally {
        isLoadingFavourites = false;
        isLoadedFavourites = true;
    }
}

/**
 * Проверяет в избранном ли офер
 * @param {Object} offer - объект описания товара
 */
function checkIsFavouriteOffer(offer) {
    if (!favouritesData.favourites.value) {
        return false;
    }

    if (Array.isArray(favouritesData.favourites.value)) {
        return favouritesData.favourites.value.includes(offer.id);
    }

    if (typeof favouritesData.settings.isFavourite === 'function') {
        try {
            return favouritesData.settings.isFavourite(favouritesData.favourites.value, offer);
        } catch (error) {
            console.error(error);
        }
    }

    return !!favouritesData.favourites.value[offer.id];
}

/** Запускает опрос избранного*/
function startFavouritesPolling() {
    if (!favouritesData.settings.pollingRate) {
        loadFavourites();
        return;
    }

    let availableNextPolling = true;

    favouritesPollingIntervalId = setInterval(async () => {
        if (availableNextPolling) {
            availableNextPolling = false;

            await loadFavourites();

            availableNextPolling = true;
        }
    }, favouritesData.settings.pollingRate);
}

/** Останавливает опрос избранного*/
function stopFavouritesPolling() {
    clearInterval(favouritesPollingIntervalId);
}

ApiEventController.on(ApiEventController.EVENTS.UPDATE_FAVOURITES, (event) => {
    if (!favouritesData.favourites) {
        return;
    }

    const detail = event.detail;

    if (detail) {
        favouritesData.favourites.value = {
            ...favouritesData.favourites?.value,
            ...detail,
        };
    } else {
        loadFavourites();
    }
});

/**
 * Composable функция, которая отвечает за работу с избранным
 * Все что находится внутри useOfferFavouritesController, является уникальным состоянием для каждого компонента офера,
 * все что находится вне, является общим состоянием для всех компонентов офера
 * @param {Object} offer - объект описания товара
 * @param {Object} favouritesSettings - объект с настройками избранного из конфига
 */
export default function useOfferFavouritesController(offer, favouritesSettings) {
    const isFavouriteOffer = ref(false);
    /** Проверяем наличие настроек избранного*/
    const hasFavouritesSettings = !!favouritesSettings?.items;
    /** Нужно ли опрашивать избранное*/
    const isNeedPollingFavourites = hasFavouritesSettings && !!favouritesSettings.pollingRate;

    /** Загружаем избранное только один раз и только если оно еще не загружена*/
    if (!isLoadedFavourites && !isLoadingFavourites) {
        /** Делаем избранное реактивным для того чтобы можно было повесить на него watch*/
        favouritesData.favourites = ref(null);
        favouritesData.settings = favouritesSettings;

        if (hasFavouritesSettings) {
            loadFavourites().then(() => {
                if (isNeedPollingFavourites) {
                    startFavouritesPolling();
                }
            });
        }
    }

    /** Проверяем в избранном ли офер */
    isFavouriteOffer.value = checkIsFavouriteOffer(offer);

    /**
     * Обработчик изменения статуса избранное
     * @param {Boolean} isFavourite
     */
    const onChangeFavouriteStatus = async (isFavourite) => {
        try {
            stopFavouritesPolling();

            const result = await favouritesData.settings.onChange({
                ...offer,
                isFavourite,
            });

            isFavouriteOffer.value = typeof result === 'boolean' ? result : isFavourite;

            window.sbEvents.pushEvent('UPDATE_FAVOURITES', { [offer.id]: isFavouriteOffer.value });
        } catch (error) {
            console.error(error);
        } finally {
            if (isNeedPollingFavourites) {
                startFavouritesPolling();
            }
        }
    };

    watch(favouritesData.favourites, () => {
        if (favouritesData.favourites.value) {
            isFavouriteOffer.value = checkIsFavouriteOffer(offer);
        }
    });

    return {
        isFavouriteOffer,
        onChange: hasFavouritesSettings && favouritesData.settings.onChange ?
            onChangeFavouriteStatus :
            () => {
            },
    };
}
